/**
 * AI Thread API Functions
 *
 * API functions for AI thread CRUD operations.
 * Routes mount at /api/ai-core/* (platform convention: /api/<pluginId>/<route>).
 */

import type { AiThread, CreateThreadDto } from '@lognai/plugin-ai-core';
import { apiFetch } from './client';

const BASE_URL = '/api/ai-core';

/**
 * Create a new AI thread
 */
export const createAiThread = async (dto: CreateThreadDto): Promise<AiThread> => {
  return apiFetch<AiThread>(`${BASE_URL}/threads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
    credentials: 'include',
  });
};

/**
 * List all AI threads for the current user
 */
export const listAiThreads = async (): Promise<AiThread[]> => {
  return apiFetch<AiThread[]>(`${BASE_URL}/threads`, {
    credentials: 'include',
  });
};

/**
 * Get a single AI thread by ID
 */
export const getAiThread = async (id: string): Promise<AiThread> => {
  return apiFetch<AiThread>(`${BASE_URL}/threads/${encodeURIComponent(id)}`, {
    credentials: 'include',
  });
};

/**
 * Update AI thread (PATCH)
 */
export interface UpdateAiThreadPatch {
  pinnedTo?: { pluginId: string; entityType: string; entityId: string } | null;
  title?: string;
}

export const updateAiThread = async (id: string, patch: UpdateAiThreadPatch): Promise<AiThread> => {
  return apiFetch<AiThread>(`${BASE_URL}/threads/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
    credentials: 'include',
  });
};
